import * as DomUtility from '@sparta.global.utilities/dom-utility/dist/es6.js';

/* global DomUtility */
/* eslint-disable no-param-reassign */

const {
  findEl,
  findAllEls,
  toggleEl,
  closestEl
} = DomUtility;
const ACTIVE = 'active';
const SHOW_HIDE = 'section[data-sparta-show-hide-id]:not(.spa-vue-component)';
const SHOW_HIDE_LIST = '.sparta-show-hide-list:not(.spa-vue-component)';
const SHOW_HIDEE = '.sparta-show-hidee';
const EXPANDALL = 'sparta-show-hide-expandall';
const COLLAPSEALL = 'sparta-show-hide-collapseall';

/**
 * The Show-Hide Utility shows and hides content.
 */
class SpartaShowHide {
  /**
   * Finds all showHiddees and applies event listeners to the panels.
   * @param {object} options - Initial options for the utility.
   * @returns {void}
   */
  init(options = {}) {
    const {
      showControls = true
    } = options;
    this.showControls = showControls;
    this.addEventHandling();
  }

  /**
   * Sets up initial event listeners.
   * @returns {void}
   */
  addEventHandling() {
    // Set element arrays
    this.setShowHideLists();
    this.setShowHideListSections();
    this.setShowHideSections();

    // event delegation for lists
    this.handleSingleShowHides();
    this.handleShowHideLists();
    this.handleControls();
    this.showHideLists.forEach(list => {
      list.setAttribute('data-spa-init', 'true');
    });
  }

  /**
   * set up event listeners for one-off show-hides
   * @returns {void}
   */
  handleSingleShowHides() {
    this.showHideSections.forEach(section => {
      this.assignListenerToSectionChildElement(section);
    });
  }

  /**
   * Sets up event listeners for a list of show/hide links.
   * @returns {void}
   */
  handleShowHideLists() {
    this.showHideLists.forEach(showHideList => {
      [...showHideList.children].forEach(child => {
        // `classList` is empty for show/hide links. Use tag name instead.
        if (child.tagName === 'SECTION') {
          this.assignListenerToSectionChildElement(child);
        }
      });
      showHideList.addEventListener("click", e => {
        if (e.target.classList.contains(EXPANDALL)) {
          e.preventDefault();
          return this.handleAll(e.target, 'open');
        }
        if (e.target.classList.contains(COLLAPSEALL)) {
          e.preventDefault();
          return this.handleAll(e.target, 'close');
        }
        return '';
      });
    });
  }

  /**
   * Returns true/false if hidee is active.
   * @param {object} hidee - The hidee DOM element.
   * @returns {boolean} isExpanded
   */
  isHideeExpanded(hidee) {
    return hidee.classList.contains(ACTIVE);
  }

  /**
   * Returns true/false if hidee is active.
   * @param {object} hidee - The hidee DOM element.
   * @returns {boolean} isExpanded
   */
  toggleHidee(hidee) {
    let isExpanded = this.isHideeExpanded(hidee); // currently expanded

    hidee.classList.toggle(ACTIVE, !isExpanded);
    isExpanded = !isExpanded; // after above change

    hidee.setAttribute('aria-hidden', !isExpanded);
    if (!isExpanded) {
      hidee.setAttribute('hidden', true);
    } else {
      hidee.removeAttribute('hidden');
    }
    return isExpanded;
  }

  /**
   * Adds a click listener to a button in a given section.
   * @param {object} section - The section DOM element.
   * @returns {void}
   */
  assignListenerToSectionChildElement(section) {
    const button = findEl('button', section);
    const span = findEl('span', section);
    const spanChevron = findEl('span.chevron', section);
    const showHidee = findEl(SHOW_HIDEE, section);
    let isExpanded = this.isHideeExpanded(showHidee);
    showHidee.setAttribute('aria-hidden', !isExpanded);
    if (!isExpanded) showHidee.setAttribute('hidden', true);
    this.setAriaExpanded(button, isExpanded);

    // on trigger click listener
    button.onclick = e => {
      e.preventDefault();
      isExpanded = this.toggleHidee(showHidee);
      toggleEl(span, ACTIVE);
      toggleEl(spanChevron, ACTIVE);
      this.setAriaExpanded(button, isExpanded);
    };
    showHidee.addEventListener('transitionend', event => {
      if (event.propertyName === 'max-height') {
        showHidee.style.maxHeight = this.isHideeExpanded(showHidee) ? '' : '0px';
      }
    });
  }

  /**
   * manage the expand/contract buttons
   * @returns {void}
   */
  handleControls() {
    if (this.showControls) {
      this.showHideLists.forEach(showHideList => {
        toggleEl(findEl('.controls', showHideList));
      });
    }
  }

  /**
   * handle a single show hide
   * @param {Object} target is the DOM obj of the event
   * @returns {void}
   */
  handleSingleShowHide(target) {
    const parent = closestEl(target, SHOW_HIDE);
    const button = findEl('button', parent);
    const span = findEl('span', button);
    const spanChevron = findEl('span.chevron', button);
    const showHidee = findEl(SHOW_HIDEE, parent);
    const isExpanded = this.toggleHidee(showHidee);
    toggleEl(span, ACTIVE);
    toggleEl(spanChevron, ACTIVE);
    this.setAriaExpanded(button, isExpanded);
    showHidee.addEventListener('transitionend', event => {
      if (event.propertyName === 'max-height') {
        showHidee.style.maxHeight = this.isHideeExpanded(showHidee) ? '' : '0px';
      }
    });
  }

  /**
   * set up event listeners for a list of show hides and delegate it to the parent
   * @param {Object} target DOM obj of the event
   * @param {String} command 'open' or 'close' commands
   * @returns {void}
   */
  handleAll(target, command) {
    const parent = closestEl(target, SHOW_HIDE_LIST);
    const showHides = findAllEls(SHOW_HIDE, parent);
    showHides.forEach(showHide => {
      const button = findEl('button', showHide);
      const showHidee = findEl(SHOW_HIDEE, showHide);
      const span = findEl('span', button);
      const spanChevron = findEl('span.chevron', button);
      const isExpanded = this.isHideeExpanded(showHidee);
      if (command === 'open' && !isExpanded) {
        this.toggleHidee(showHidee);
        toggleEl(button, ACTIVE);
        toggleEl(span, ACTIVE);
        toggleEl(spanChevron, ACTIVE);
        this.setAriaExpanded(button, true);
      } else if (command === 'close' && isExpanded) {
        this.toggleHidee(showHidee);
        toggleEl(button, ACTIVE);
        toggleEl(span, ACTIVE);
        toggleEl(spanChevron, ACTIVE);
        this.setAriaExpanded(button, false);
      }
      showHidee.addEventListener('transitionend', event => {
        if (event.propertyName === 'max-height') {
          showHidee.style.maxHeight = this.isHideeExpanded(showHidee) ? '' : '0px';
        }
      });
    });
  }

  /**
   * find and setup all module show hide lists
   * @returns {void}
   */
  setShowHideLists() {
    this.showHideLists = findAllEls(`${SHOW_HIDE_LIST}:not([data-spa-init="true"])`);
  }

  /**
   * find and setup all togglelist sections
   * @returns {void}
   */
  setShowHideListSections() {
    this.showHideListSections = [].concat(...this.showHideLists.map(showHideList => findAllEls(SHOW_HIDE, showHideList)));
  }

  /**
   * find and setup all other show hide sections not in a list
   * @returns {void}
   */
  setShowHideSections() {
    this.showHideSections = findAllEls(SHOW_HIDE).filter(section => this.showHideListSections.indexOf(section) === -1);
  }

  /**
   * Sets `aria-expanded` attribute for a button.
   * @param {object} element - The button DOM element.
   * @param {boolean} isExpanded - Whether the element should be expanded or not.
   * @returns {void}
   */
  setAriaExpanded(element, isExpanded) {
    element.setAttribute('aria-expanded', isExpanded);
  }
}
var index = new SpartaShowHide();

export { index as default };
